import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Alert, LinearProgress, Switch, FormControlLabel, Chip,
  Divider, Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  ManageAccounts as ManageAccountsIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { authGet, authPut, authPost } from '../../services/apiService';
import { format } from 'date-fns';

const darkGreenColor = '#1D4434';

const ServiceManagementAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [pullResult, setPullResult] = useState(null);

  const [epicUrl, setEpicUrl] = useState('');
  const [pullEnabled, setPullEnabled] = useState(false);

  const [status, setStatus] = useState({
    lastPulledAt: null,
    lastPullStatus: 'never',
    lastPullError: '',
    parsedProject: null,
    parsedEpicId: null
  });

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await authGet('/api/reliability/service-management/config');
      if (res.success) {
        setEpicUrl(res.config.epicUrl || '');
        setPullEnabled(!!res.config.pullEnabled);
        setStatus({
          lastPulledAt: res.config.lastPulledAt || null,
          lastPullStatus: res.config.lastPullStatus || 'never',
          lastPullError: res.config.lastPullError || '',
          parsedProject: res.config.parsedProject || null,
          parsedEpicId: res.config.parsedEpicId || null
        });
      } else {
        setError('Failed to load configuration');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const res = await authPut('/api/reliability/service-management/config', { epicUrl, pullEnabled });
      if (res.success) {
        setSuccess('Configuration saved successfully');
        setTimeout(() => setSuccess(null), 5000);
        await loadConfig();
      } else {
        setError(res.message || 'Failed to save configuration');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handlePullNow = async () => {
    try {
      setPulling(true);
      setError(null);
      setPullResult(null);
      const res = await authPost('/api/reliability/service-management/pull', {});
      if (res.success) {
        setPullResult(res.message);
        await loadConfig();
      } else {
        setError(res.message || 'Pull failed');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Pull failed');
    } finally {
      setPulling(false);
    }
  };

  const disabled = loading || saving || pulling;
  const canPull = epicUrl.trim().length > 0;

  const statusChip = () => {
    if (status.lastPullStatus === 'success') {
      return <Chip icon={<CheckCircleIcon />} label="Last pull succeeded" color="success" size="small" variant="outlined" />;
    }
    if (status.lastPullStatus === 'error') {
      return (
        <Tooltip title={status.lastPullError || 'Unknown error'}>
          <Chip icon={<ErrorIcon />} label="Last pull failed" color="error" size="small" variant="outlined" />
        </Tooltip>
      );
    }
    return <Chip label="Never pulled" size="small" variant="outlined" />;
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <ManageAccountsIcon sx={{ mr: 1, color: darkGreenColor }} />
        <Typography variant="h6">Service Management — Configuration</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Paste the ADO epic URL for Service Management. The project name and epic ID are parsed
        from the URL automatically. Enable daily pull to refresh features each night at 2:00 AM IST.
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Service Management Epic URL"
              value={epicUrl}
              onChange={e => setEpicUrl(e.target.value)}
              placeholder="https://dev.azure.com/{org}/{project}/_workitems/edit/{id}"
              helperText="Full ADO work-item URL — org, project, and epic ID are read from the URL"
              disabled={disabled}
              size="small"
            />
          </Grid>

          {/* Show parsed values as confirmation */}
          {(status.parsedProject || status.parsedEpicId) && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <InfoIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  Parsed: project <strong>{status.parsedProject}</strong>, epic ID <strong>{status.parsedEpicId}</strong>
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mt={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={pullEnabled}
                    onChange={e => setPullEnabled(e.target.checked)}
                    disabled={disabled}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Enable daily pull</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically fetches features every night at 2:00 AM IST
                    </Typography>
                  </Box>
                }
              />
              {pullEnabled && (
                <Chip
                  icon={<ScheduleIcon fontSize="small" />}
                  label="Daily at 2:00 AM IST"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={disabled}
                startIcon={<SaveIcon />}
                sx={{ bgcolor: darkGreenColor, '&:hover': { bgcolor: '#0f2e1f' } }}
              >
                {saving ? 'Saving…' : 'Save Configuration'}
              </Button>

              <Button
                variant="outlined"
                onClick={handlePullNow}
                disabled={disabled || !canPull}
                startIcon={<SyncIcon />}
                color="success"
              >
                {pulling ? 'Pulling…' : 'Pull Now'}
              </Button>
            </Box>
          </Grid>

          {(status.lastPulledAt || status.lastPullStatus !== 'never') && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {statusChip()}
                {status.lastPulledAt && (
                  <Typography variant="caption" color="text.secondary">
                    Last pulled: {format(new Date(status.lastPulledAt), 'dd MMM yyyy, HH:mm')}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {success && <Grid item xs={12}><Alert severity="success">{success}</Alert></Grid>}
          {pullResult && <Grid item xs={12}><Alert severity="success">{pullResult}</Alert></Grid>}
          {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
        </Grid>
      )}
    </Paper>
  );
};

export default ServiceManagementAdmin;
